import { Fix64 } from "../maths/fix64";
import { Cell } from "../world/cell";
import { TargetPriority } from "./command";

export class Enemy {
  id = 0;
  archetype = 0;
  alive = false;
  isAir = false;
  isBoss = false;
  /** A rare spawn (sim/rares.ts): a boss for targeting, but stuns, slows and full area damage take it (Kevin 2026-10-25). */
  isRare = false;
  hp = Fix64.Zero;
  maxHp = Fix64.Zero;
  speedPerTick = Fix64.Zero;
  armour = Fix64.Zero;
  distance = Fix64.Zero;
  x = Fix64.Zero;
  y = Fix64.Zero;
  /** Integer cell of (x, y), kept for cheap range prefilters. */
  cx = 0;
  cy = 0;
  points = 0;
  leakDamage = 0;
  slowFraction = Fix64.Zero;
  slowTicksLeft = 0;
  /**
   * Burning (DESIGN §6.1, Fire): one stack per Fire tower that has lit this enemy, up to the archetype's
   * `burnMaxStacks`, each with its own damage per tick and ticks left (parallel arrays, in the order they were lit).
   * The three fields after them are the stacks summed up — total damage per tick, the longest time left, the tower
   * that lit or refreshed a stack last (credited with burn kills) — so views need not read the stacks.
   */
  readonly burnStackSource: number[] = [];
  readonly burnStackPerTick: Fix64[] = [];
  readonly burnStackTicks: number[] = [];
  burnPerTick = Fix64.Zero;
  burnTicksLeft = 0;
  burnSourceTowerId = 0;
  shredStacks = 0;
  /** Armour taken off by shred stacks: each stack removes the shredding tower's `shredPerHit` (towers.json). */
  shredArmour = Fix64.Zero;
  /** Flyer boost: ticks of boosted speed left, and ticks until the boost may trigger again. */
  boostTicksLeft = 0;
  boostCooldownLeft = 0;
  /** Hits taken (Necromancer spawns a Grunt every few). */
  hitsTaken = 0;
  /** Set once the enemy has walked over a Toll gate: its kill points carry the toll bonus. */
  tolled = false;
  /** Ticks until a blocked enemy strikes the troop holding it again. */
  attackCooldown = 0;
  /** Physical armour lent by a nearby Shieldbearer this tick (recomputed every tick before the towers fire). */
  shieldArmour = Fix64.Zero;
  /** Elite affix index into `balance.affixes` (DESIGN §5.1), −1 for a plain enemy. */
  affix = -1;
  /** The stage power (HP multiplier on the archetype) it spawned at, before any elite HP: what a Splitting elite's splits get. */
  power = Fix64.One;
  /**
   * Hex Totem curse (TOWERS_V2), recomputed every tick after movement: the extra damage it takes from every source
   * (0 = not hexed), the healing cut on it, and the totem whose curse it wears (0 = none). Hexes never stack.
   */
  hexBonus = Fix64.Zero;
  hexHealCut = Fix64.Zero;
  hexTowerId = 0;
  /** Held by a troop or a Barracks soldier this tick (set by enemy movement; a Mortar leads only a moving target). */
  blocked = false;
  /**
   * The M21 abilities (systems/enemyAbilities.ts, docs/ENEMIES_V2.md): what the enemy is doing, the ticks left in it,
   * the ticks until it may cast or stomp again (−1 = not primed yet: set from the archetype's first delay on its first
   * tick), the dives it has used, the road distance a dive surfaces at, and for a summoned imp its summoner's id.
   */
  act = EnemyAct.None;
  actTicks = 0;
  abilityCooldown = -1;
  burrowsUsed = 0;
  surfaceAt = Fix64.Zero;
  summonerId = 0;
  /** Underground: no tower, shell, soldier or troop can find it (only what "sees burrowed"; systems/targeting.ts). */
  get burrowed(): boolean { return this.act === EnemyAct.Burrowed; }
  /**
   * Tier-4 statuses (M21, sim/branches; zero on a run where no branch is chosen). `stunTicksLeft`: it stands still (a
   * stun, a freeze, a root). `dot*`: one damage-over-time slot (poison arrows, bleed, contagion; the strongest is kept,
   * refreshed by its own source), magic damage credited to `dotSourceId`. `sheepTicksLeft`: polymorphed (slow,
   * unarmoured). `auraSlow`: the strongest slow from a Blizzard aura, a zone or a Weakness curse, recomputed every tick
   * (it does not stack with a Frost hit's slow: the larger applies). `frostbiteTicks`: ticks spent inside a Frostbite
   * aura in a row. `ambushed`: a Rangers' Camp has stunned it once already.
   */
  stunTicksLeft = 0;
  dotPerTick = Fix64.Zero;
  dotTicksLeft = 0;
  dotSourceId = 0;
  sheepTicksLeft = 0;
  auraSlow = Fix64.Zero;
  frostbiteTicks = 0;
  ambushed = false;
  /** A troop holding it takes this share of its max HP a strike instead of the troops' `enemyHitFraction` (the wild pet's bite); null = the rule's. */
  hitFraction: Fix64 | null = null;
  /**
   * Pets v3 statuses (sim/petStatus.ts; zero on a run without a Pets v3 pet): the pet's own burn (damage per tick, ticks
   * left, apart from the towers' stacks), a mark (extra damage taken and extra kill points, as fractions, ticks left) and
   * a dazzle (it strikes no troop while it lasts).
   */
  petBurnPerTick = Fix64.Zero;
  petBurnTicks = 0;
  markTaken = Fix64.Zero;
  /** The mark's extra kill points, in percent (whole points: never a fraction lost to rounding). */
  markPointsPercent = 0;
  markTicks = 0;
  dazzleTicks = 0;
  /** Immune unit (M36, sim/immunity.ts): `Immunity` bits, 0 = none. Hashed when nonzero. */
  immunity = 0;
  /** @internal (tower id, tick) pairs pacing the EnemyImmune popups; never hashed. */
  readonly immuneNotes: number[] = [];
}

/**
 * What an enemy with an ability is doing (docs/ENEMIES_V2.md). Summoning, Digging, Surfacing and WindingUp stand it
 * still on the road (a telegraph the player can punish); Burrowed moves it underground, out of reach.
 */
export enum EnemyAct { None = 0, Summoning = 1, Digging = 2, Burrowed = 3, Surfacing = 4, WindingUp = 5 }

/** A base troop (DESIGN §7): walks to the rally flag, holds, fights ground enemies within reach, blocks them while alive. */
export class Troop {
  id = 0;
  alive = false;
  hp = Fix64.Zero;
  maxHp = Fix64.Zero;
  damage = Fix64.Zero;
  x = Fix64.Zero;
  y = Fix64.Zero;
  cx = 0;
  cy = 0;
  /** Remaining cells to walk (next first). Empty = holding. */
  path: Cell[] = [];
  attackCooldown = 0;
  /** The Barracks tower this soldier belongs to (TOWERS_V2); 0 for a fortress troop. */
  ownerTowerId = 0;
  /** Cuts a blocked enemy's blow on it (Barracks tiers); 0 for a fortress troop. */
  armour = Fix64.Zero;
  /** A fallen Barracks soldier: ticks until it musters again at its tower. */
  respawnLeft = 0;
  /** Tier 4, Shield Wall: how much further than the troops' block range a knight holds the road (0 otherwise). */
  blockBonus = Fix64.Zero;
  /** Pets v3, Crystal Ward: a shield that takes blows before its health, for `shieldTicks` (fortress troops only). */
  shield = Fix64.Zero;
  shieldTicks = 0;
}

export class Tower {
  id = 0;
  archetype = 0;
  alive = false;
  cell = new Cell(0, 0);
  tier = 1;
  cooldown = 0;
  priority = TargetPriority.First;
  kills = 0;
  /** Disabled status (Bomber): ticks left during which the tower neither targets nor fires. */
  disabledTicksLeft = 0;
  auraDamageMult = Fix64.One;
  auraPointsMult = Fix64.One;
  /** Arcane Spire (TOWERS_V2): the enemy its beam is on (0 = none) and the ticks it has held it (the ramp). */
  beamTarget = 0;
  beamTicks = 0;
  /**
   * Tier 4 (M21, sim/branches): shots fired (every-Nth-shot skills), a skill's cooldown and active window (Overheat,
   * Rally Cry, Volley, Polymorph, Banish, Thorn Trap…), the Tesla's charge, extra shots left in a burst, a zone cooldown,
   * the Sorcerer's extra beams (enemy ids, 0 = none), and what auras give it: fire-rate haste (War Banner, Rally Cry)
   * and range (Guiding Light).
   */
  shotCount = 0;
  skillTimer = 0;
  skillActive = 0;
  charge = 0;
  burstLeft = 0;
  zoneTimer = 0;
  readonly extraBeams: number[] = [];
  auraHaste = Fix64.Zero;
  rallyHaste = Fix64.Zero;
  auraRangeBonus = Fix64.Zero;
}

/**
 * A patch of ground a tier-4 skill leaves on the road (M21): Thorn Trap, Firestorm, Plague clouds, Earthquake, Napalm.
 * Every ground enemy within `radius` of (x, y) takes `damagePerTick` (magic; burn for fire zones) and wears `slow` while
 * it stands there, until `ticksLeft` runs out.
 */
export interface GroundZone {
  readonly id: number;
  readonly towerId: number;
  readonly kind: ZoneKind;
  readonly x: Fix64;
  readonly y: Fix64;
  readonly radius: Fix64;
  readonly damagePerTick: Fix64;
  readonly slow: Fix64;
  readonly ticks: number;
  ticksLeft: number;
}

export enum ZoneKind { Thorns = 0, Firestorm = 1, PlagueCloud = 2, Earthquake = 3, Napalm = 4 }

/** A Mortar shell in flight (TOWERS_V2): it lands on (x, y) when `ticksLeft` reaches 0 and hits every ground enemy within `radius`. */
export interface Shell {
  readonly id: number;
  readonly towerId: number;
  readonly archetype: number;
  /** The landing point (a Seeker Shell's moves onto its target as it lands). */
  x: Fix64;
  y: Fix64;
  readonly radius: Fix64;
  readonly damage: Fix64;
  readonly flightTicks: number;
  ticksLeft: number;
  /** Tier 4, Seeker Shells: the enemy it homes on (0 = an ordinary shell); its burst also hits flyers. */
  readonly seekId: number;
}

/**
 * Ascend (M21): `index` is the tower archetype whose branch it chooses (`ascend.<tower>`). Skill: `index` is the global
 * skill index in `balance.branches.skills` (`skill.<tower>.<branch>.<a|b>`).
 */
/**
 * Move (M55): relocate a standing tower — tier, branch and kills kept — to another legal square. Dealt in a road slot of
 * an in-run hand once `cards.moveCard.minTowers` towers stand (offerGenerator); `index` is always 0.
 */
export enum CardKind { Path = 0, Tower = 1, Upgrade = 2, Ascend = 3, Skill = 4, Move = 5 }

export interface Card { readonly kind: CardKind; readonly index: number }

export function sameCard(a: Card, b: Card): boolean { return a.kind === b.kind && a.index === b.index; }

export enum OfferKind { OpeningPath = 0, OpeningTower = 1, PostWave = 2 }
